import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { FC, useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { PAGE_SIZE } from "../../constants"
import { usePaginationState } from "../../hooks/usePaginationState"
import { LabReport } from "../../types/LabReport"
import { classNames } from "../../utils/classNames"
import { FilterConfig, filterData, getFilterOptions } from "../../utils/data"
import { Pagination } from "../common"
import { DebouncedInput } from "../common/DebouncedInput"
import { Filters, VariableFilter } from "../common/Filters/Filters"
import { StatusBadge } from "../common/StatusBadge"
import { fuzzyFilter } from "../common/Tables/utils"

interface Props {
  labReports: LabReport[]
  isAnonymous?: boolean
}

export const PatientLabReportsTable: FC<Props> = ({ labReports, isAnonymous = false }) => {
  // General
  const navigate = useNavigate()

  const columnHelper = createColumnHelper<LabReport>()
  const columns = [
    columnHelper.accessor("description", {
      header: "Test Type",
      cell: (info) => (
        <span className="block max-w-[20ch] truncate lg:max-w-[35ch]">{info.getValue() || "Unknown"}</span>
      ),
    }),
    columnHelper.accessor("status", {
      header: "Status",
      cell: (info) => <StatusBadge status={info.getValue() || "Unknown"} />,
    }),
    columnHelper.accessor("receivedAt", {
      header: "Reported At",
      cell: (info) => (
        <span className="block max-w-[35ch] truncate">
          {info.getValue()
            ? new Intl.DateTimeFormat("en-US", {
                month: "short",
                day: "2-digit",
                year: "numeric",
              }).format(new Date(info.getValue()))
            : null}
        </span>
      ),
    }),
    // Only show the actions column if not in anonymous mode
    ...(isAnonymous
      ? []
      : [
          columnHelper.display({
            id: "actions",
            cell: (props: any) => {
              return (
                <span className="flex items-center justify-end gap-x-2">
                  <Link
                    to={`/organizations/${props.row.original.orgId}/lab-reports/${props.row.original.id}`}
                    className="text-primary-600 hover:text-primary-900 text-sm font-medium">
                    Details
                  </Link>
                </span>
              )
            },
          }),
        ]),
  ]

  const { setPage, paginationState } = usePaginationState({
    paramName: "labReportsPage",
    pageSize: PAGE_SIZE,
  })

  // Table Data
  const [filters, setFilters] = useState<VariableFilter[]>([])
  const filterConfigs: FilterConfig[] = [
    { key: "source", title: "Source" },
    { key: "status", title: "Status" },
    { key: "doctorName", title: "Doctor" },
    { key: "description", title: "Test type" },
  ]
  const filterOptions = getFilterOptions(labReports, filterConfigs)
  const [filteredData, setFilteredData] = useState(labReports)
  useEffect(() => {
    setFilteredData(filterData(labReports, filters))
  }, [labReports, filters])

  const [search, setSearch] = useState("")
  const table = useReactTable({
    data: filteredData,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      globalFilter: search,
      pagination: paginationState,
    },
    onGlobalFilterChange: setSearch,
    onPaginationChange: (updater) => {
      if (typeof updater === "function") {
        const newState = updater(paginationState)
        setPage(newState.pageIndex + 1)
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  return (
    <div className="card">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <h2 className="text-lg font-semibold text-gray-900 lg:shrink-0">Lab Reports</h2>
        <div className="flex items-stretch justify-between">
          <div className="flex items-center gap-x-2">
            <div>
              <label htmlFor="search" className="sr-only">
                Search lab reports
              </label>
              <DebouncedInput
                name="search"
                type="search"
                className="input mt-0 h-auto py-1.5"
                placeholder="Search"
                value={search}
                onChange={(value) => setSearch(value)}
              />
            </div>
            <Filters
              viewName="labReports"
              buttonClassName="text-sm py-1.5"
              filters={filters}
              setFilters={setFilters}
              filterOptions={filterOptions}
            />
          </div>
        </div>
      </div>
      {labReports.length === 0 ? (
        <div className="mt-4 text-gray-600">No lab reports found.</div>
      ) : (
        <>
          <div className="mt-4 flow-root overflow-hidden">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header, i) => (
                          <th
                            scope="col"
                            className={classNames(
                              "relative py-2 text-left text-sm font-semibold text-gray-900",
                              i === 0
                                ? "pr-5 pl-3 sm:pl-0"
                                : i === headerGroup.headers.length - 1
                                  ? "pr-3 pl-2 sm:pr-0"
                                  : "px-2",
                            )}
                            key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {table.getRowModel().rows.length > 0 ? (
                      table.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map((cell, i) => (
                            <td
                              className={classNames(
                                "font-regular py-2 text-sm whitespace-nowrap text-gray-500",
                                i === 0
                                  ? "pr-5 pl-3 sm:pl-0"
                                  : i === row.getVisibleCells().length - 1
                                    ? "pr-3 pl-2 sm:pr-0"
                                    : "px-2",
                              )}
                              key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, {
                                ...cell.getContext(),
                                navigate,
                              })}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          className="font-regular px-4 py-4 text-center text-sm whitespace-nowrap text-gray-500"
                          colSpan={table.getHeaderGroups()?.[0]?.headers?.length || 100}>
                          No lab reports found for the current filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div>
            <Pagination
              page={table.getState().pagination.pageIndex + 1}
              pageCount={table.getPageCount()}
              hasPrevious={table.getCanPreviousPage()}
              onPrevious={() => table.previousPage()}
              hasNext={table.getCanNextPage()}
              onNext={() => table.nextPage()}
              setPage={(i) => table.setPageIndex(i - 1)}
            />
          </div>{" "}
        </>
      )}
    </div>
  )
}
